import plur from 'plur';
import { capitalize, isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import { consentActionLabel, consentActionOf } from '@/features/condition-builder';
import { inboundChannelLabel } from './inbound-channels';
import { LOG_TYPE, parseLogType } from './activity-log-types';
import type { ActivityLog } from './types';

/**
 * The Activity Name fallback label, for the cases derivable from the log payload.
 *
 * The identifier-aware attribute-change / profile-create labels count how many changed attribute
 * keys are *enabled identifiers*, using the workspace's `identifiers` config (`{ email, id,
 * cio_id, ... }` → numeric state; `0` = disabled) for person logs, or the object identifier keys
 * for object logs. Pass `identifiers` from `useActivityLogEnvironment`; when it's absent the count
 * degrades to 0 (no `(N identifier)` suffix).
 */

/** Workspace identifier config: identifier key → numeric state (`0` = disabled). */
export type IdentifierConfig = Record<string, number>;

// Attribute-change handling covers person + object types only, NOT relationship (`_r:*`) types —
// those fall through to the raw name (typically empty).
const ATTRIBUTE_CHANGE_TYPES: string[] = [LOG_TYPE.AttributeChange, LOG_TYPE.ObjectAttributeChange];
const FAILED_ATTRIBUTE_CHANGE_TYPES: string[] = [LOG_TYPE.FailedAttributeChange, LOG_TYPE.ObjectFailedAttributeChange];

const OBJECT_IDENTIFIER_KEYS = ['cio_object_id', 'object_id'];

function attrsRecord(log: ActivityLog): Record<string, unknown> | null {
  return isRecord(log.attrs) ? log.attrs : null;
}

/** Whether an attribute key is an enabled identifier — object identifier keys, or an enabled
 * person identifier (present in the config with a non-zero state). */
function isEnabledIdentifier(name: string, identifiers: IdentifierConfig, isObject: boolean): boolean {
  if (isObject) return OBJECT_IDENTIFIER_KEYS.includes(name);
  return name in identifiers && identifiers[name] !== 0;
}

function attributeChangeName(
  log: ActivityLog,
  isFailed: boolean,
  identifiers: IdentifierConfig,
  isObject: boolean,
): string {
  if (!isEmptyString(log.name)) return log.name;
  const names = Object.keys(attrsRecord(log) ?? {});
  const identifierCount = names.filter((name) => isEnabledIdentifier(name, identifiers, isObject)).length;

  if (names.length === 1) {
    return `${names[0]}${identifierCount > 0 ? ' (identifier)' : ''}`;
  }

  const identifierMessage = identifierCount > 0 ? ` (${identifierCount} ${plur('identifier', identifierCount)})` : '';
  return `${names.length} attributes${identifierMessage} ${isFailed ? 'failed to change' : 'changed'}`;
}

function profileCreateName(log: ActivityLog, identifiers: IdentifierConfig, isObject: boolean): string {
  // Join the identifier attribute names (person identifiers minus cio_id, plus object_id for
  // objects), suffixed with the pluralized word only (no count).
  const names = Object.keys(attrsRecord(log) ?? {});
  const identifierAttributes = names.filter(
    (name) => (isEnabledIdentifier(name, identifiers, isObject) && name !== 'cio_id') || name === 'object_id',
  );
  return `${identifierAttributes.join(', ')} (${plur('identifier', identifierAttributes.length)})`;
}

function sourceDrivenName(log: ActivityLog, verb: 'deletion' | 'merge'): string {
  const sources = log.sources ?? [];
  if (sources.some((s) => s.type === 'ui')) return verb === 'merge' ? 'Manual merge' : 'Manual deletion';
  if (verb === 'merge' && sources.some((s) => s.subtype === 'auto_merge')) return 'Multi-identifier merge';
  if (sources.some((s) => s.site_id != null)) return verb === 'merge' ? 'API merge' : 'API deletion';
  return verb === 'merge' ? 'Manual merge' : 'Deletion';
}

function failedLlmActionName(log: ActivityLog): string {
  const reason = attrsRecord(log)?.reason;
  if (typeof reason === 'string') {
    const match = reason.match(/type:\s*(\w+)/);
    if (match?.[1] != null) return match[1];
  }
  return 'LLM error';
}

function hasConsentScopeValue(value: unknown): boolean {
  if (typeof value === 'string') return value.length > 0;
  return typeof value === 'number';
}

/** The most specific scope a consent record names — topic, then sender, then channel. */
function consentScope(log: ActivityLog): string | null {
  const attrs = attrsRecord(log);
  if (attrs == null) return null;
  if (hasConsentScopeValue(attrs.topic_id)) return 'Topic';
  if (hasConsentScopeValue(attrs.sender_id)) return 'Sender';
  if (hasConsentScopeValue(attrs.channel)) return 'Channel';
  return null;
}

/**
 * "Topic opted out", "Sender opted in", or the bare action for a profile-wide record. Display
 * only: the wire name stays `opted_in` / `opted_out` / `voided`, which the name filter matches.
 */
function consentName(log: ActivityLog): string {
  const action = consentActionOf(log.name);
  if (action == null) return capitalize(log.name.replaceAll('_', ' '));

  const label = consentActionLabel(action);
  const scope = consentScope(log);
  if (scope == null) return label;
  return `${scope} ${label.toLowerCase()}`;
}

/**
 * Variant ids are opaque config ids (`variant_2`), so the name follows `variant_role` instead.
 * Holdout enrollment shares the `exposed` event name.
 */
function experimentExposureName(log: ActivityLog): string {
  const role = attrsRecord(log)?.variant_role;
  if (role === 'holdout') return 'Enrolled in holdout';
  if (role === 'control' || role === 'treatment') return `Exposed to ${role}`;
  return 'Exposed';
}

export function activityLogDisplayName(log: ActivityLog, identifiers: IdentifierConfig = {}): string {
  const parsed = parseLogType(log.type);
  const isFailedAttr = FAILED_ATTRIBUTE_CHANGE_TYPES.includes(parsed);
  const isObject = parsed.startsWith('_o:');

  if (ATTRIBUTE_CHANGE_TYPES.includes(parsed) || isFailedAttr) {
    return attributeChangeName(log, isFailedAttr, identifiers, isObject);
  }
  if (parsed === LOG_TYPE.ProfileCreate || parsed === LOG_TYPE.ObjectCreate) {
    return profileCreateName(log, identifiers, isObject);
  }
  if (parsed === LOG_TYPE.ProfileDelete) return sourceDrivenName(log, 'deletion');
  if (parsed === LOG_TYPE.ObjectDelete) return 'Deletion';
  if (parsed === LOG_TYPE.ProfileMerge) return sourceDrivenName(log, 'merge');
  if (log.type === LOG_TYPE.FailedLlmAction) return failedLlmActionName(log);
  // Geofence transitions carry the activity (`enter` / `dwell` / `exit`) in
  // `name`; present it capitalized ("Enter" / "Dwell" / "Exit").
  if (parsed === LOG_TYPE.Geofence) return capitalize(log.name);
  if (parsed === LOG_TYPE.Consent) return consentName(log);
  if (parsed === LOG_TYPE.ExperimentExposure) return experimentExposureName(log);
  if (parsed === LOG_TYPE.InboundMessage) return inboundChannelLabel(log.name);
  return log.name;
}
